// Public interview page (NO AUTH). Tim opens this on his phone, records voice
// answers per question, server transcribes via Lovable AI Gateway STT.
import { createFileRoute, notFound } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import {
  getSessionByToken,
  saveAnswer,
  completeInterview,
} from "@/lib/interview.functions";
import logoAsset from "@/assets/durafoam-logo.webp.asset.json";
import { Mic, Square, ArrowLeft } from "lucide-react";

function normalizeToken(token: string) {
  try {
    return decodeURIComponent(token).trim();
  } catch {
    return token.trim();
  }
}

function isUsableToken(token: string) {
  const normalized = normalizeToken(token);
  const lower = normalized.toLowerCase();
  if (["<token>", "{token}", ":token", "$token", "token"].includes(lower)) return false;
  return /^[a-z0-9_-]{8,200}$/i.test(normalized);
}

export const Route = createFileRoute("/interview/$token")({
  ssr: false,
  loader: async ({ params }) => {
    const token = normalizeToken(params.token);
    if (!isUsableToken(token)) throw notFound();
    const res = await getSessionByToken({ data: { token } });
    if (!res) throw notFound();
    return res;
  },
  head: () => ({
    meta: [
      { title: "Durafoam Roofing Interview" },
      { name: "robots", content: "noindex,nofollow" },
      { name: "viewport", content: "width=device-width, initial-scale=1, maximum-scale=1" },
    ],
  }),
  errorComponent: ({ error }) => (
    <div className="min-h-screen flex items-center justify-center p-6 text-center">
      <p className="text-sm text-slate-600">Could not load this interview. {error instanceof Error ? error.message : String(error)}</p>
    </div>
  ),
  notFoundComponent: () => (
    <div className="min-h-screen flex items-center justify-center p-6 text-center">
      <div>
        <h1 className="text-xl font-semibold text-[#0F1A42]">Link not found</h1>
        <p className="mt-2 text-sm text-slate-600">
          This interview link is invalid, missing its unique token, or has already been used.
        </p>
      </div>
    </div>
  ),
  component: InterviewPage,
});

type Question = { id: string; question_text: string; order: number };

function InterviewPage() {
  const data = Route.useLoaderData();
  const { token: tokenParam } = Route.useParams();
  const token = normalizeToken(tokenParam);
  const session = data.session;
  const questions: Question[] = ((session.questions ?? []) as Question[])
    .slice()
    .sort((a, b) => a.order - b.order);
  const initialAnswers: Record<string, string> = {};
  for (const a of data.answers) initialAnswers[a.question_id] = a.transcript;

  const [started, setStarted] = useState(false);
  const [completed, setCompleted] = useState(session.status === "awaiting_ai_draft");
  const [idx, setIdx] = useState(() => {
    // resume at first unanswered question
    const i = questions.findIndex((q) => !initialAnswers[q.id]);
    return i === -1 ? 0 : i;
  });
  const [answers, setAnswers] = useState<Record<string, string>>(initialAnswers);

  const save = useServerFn(saveAnswer);
  const complete = useServerFn(completeInterview);

  if (completed) {
    return <DoneScreen />;
  }

  if (!started) {
    return (
      <Shell>
        <h1 className="mt-6 text-2xl font-bold text-[#0F1A42]">
          Quick interview for a new blog post
        </h1>
        <p className="mt-2 text-base text-slate-600">{session.topic}</p>
        <p className="mt-6 text-[15px] leading-relaxed text-slate-700">
          We'll ask you 10 questions. Just tap the microphone and answer naturally
          — like you're explaining it to a customer. Takes about 10–15 minutes.
          Your answers will be turned into a blog post for you to review before it
          goes live.
        </p>
        <button
          onClick={() => setStarted(true)}
          className="mt-8 w-full rounded-xl bg-[#D97706] py-4 text-lg font-semibold text-white shadow-sm active:translate-y-px"
        >
          Start interview
        </button>
      </Shell>
    );
  }

  const q = questions[idx];
  const total = questions.length;

  return (
    <Shell>
      <div className="mt-4 flex items-center justify-between text-xs font-medium text-slate-500">
        <button
          type="button"
          onClick={() => setIdx((i) => Math.max(0, i - 1))}
          disabled={idx === 0}
          className="inline-flex items-center gap-1 disabled:opacity-30"
        >
          <ArrowLeft className="h-3 w-3" /> Back
        </button>
        <span>
          Question {idx + 1} of {total}
        </span>
        <span />
      </div>
      <div className="mt-3 h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
        <div
          className="h-full rounded-full bg-[#D97706] transition-all"
          style={{ width: `${((idx + 1) / total) * 100}%` }}
        />
      </div>

      <h2 className="mt-8 text-xl font-semibold leading-snug text-[#0F1A42]">
        {q.question_text}
      </h2>

      <Recorder
        key={q.id}
        token={token}
        initialTranscript={answers[q.id] ?? ""}
        onTranscript={(t) => setAnswers((prev) => ({ ...prev, [q.id]: t }))}
      />

      <div className="mt-8 flex flex-col gap-3">
        <button
          type="button"
          disabled={!answers[q.id]?.trim()}
          onClick={async () => {
            const transcript = (answers[q.id] ?? "").trim();
            if (!transcript) return;
            await save({
              data: {
                token,
                question_id: q.id,
                question_text: q.question_text,
                transcript,
              },
            });
            if (idx + 1 >= total) {
              await complete({ data: { token } });
              setCompleted(true);
            } else {
              setIdx(idx + 1);
            }
          }}
          className="w-full rounded-xl bg-[#D97706] py-4 text-lg font-semibold text-white shadow-sm disabled:opacity-40 active:translate-y-px"
        >
          {idx + 1 >= total ? "Finish interview" : "Next question →"}
        </button>
      </div>
    </Shell>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-white">
      <div className="mx-auto w-full max-w-xl px-5 pb-16 pt-8">
        <img src={logoAsset.url} alt="Durafoam Roofing" width={40} height={40} className="h-10 w-auto" />
        {children}
      </div>
    </div>
  );
}

function DoneScreen() {
  return (
    <Shell>
      <div className="mt-16 text-center">
        <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-[#D97706]/10 text-3xl">
          ✓
        </div>
        <h1 className="mt-6 text-2xl font-bold text-[#0F1A42]">All done — thank you!</h1>
        <p className="mt-3 text-[15px] leading-relaxed text-slate-700">
          We're turning your answers into a blog post now. The team will review
          it before it's published. Thanks for sharing your expertise!
        </p>
      </div>
    </Shell>
  );
}

/* -------- Recorder component (MediaRecorder + server transcription) -------- */

function pickMime(): string {
  const candidates = ["audio/mp4", "audio/webm;codecs=opus", "audio/webm"];
  const MR: any = (typeof window !== "undefined" && (window as any).MediaRecorder) || null;
  if (!MR) return "audio/webm";
  for (const c of candidates) {
    try {
      if (MR.isTypeSupported && MR.isTypeSupported(c)) return c;
    } catch {}
  }
  return "audio/webm";
}

function extFor(mime: string): string {
  if (mime.startsWith("audio/mp4")) return "mp4";
  if (mime.startsWith("audio/webm")) return "webm";
  if (mime.startsWith("audio/mpeg")) return "mp3";
  if (mime.startsWith("audio/wav")) return "wav";
  return "webm";
}

function Recorder({
  token,
  initialTranscript,
  onTranscript,
}: {
  token: string;
  initialTranscript: string;
  onTranscript: (t: string) => void;
}) {
  const [recording, setRecording] = useState(false);
  const [busy, setBusy] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [transcript, setTranscript] = useState(initialTranscript);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const streamRef = useRef<MediaStream | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    setTranscript(initialTranscript);
  }, [initialTranscript]);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      streamRef.current?.getTracks().forEach((t) => t.stop());
    };
  }, []);

  async function start() {
    setError(null);
    if (typeof navigator === "undefined" || !navigator.mediaDevices?.getUserMedia) {
      setError("Microphone not available on this device.");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true },
      });
      streamRef.current = stream;
      const mime = pickMime();
      const rec = new MediaRecorder(stream, { mimeType: mime });
      chunksRef.current = [];
      rec.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };
      rec.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
        if (timerRef.current) clearInterval(timerRef.current);
        const blob = new Blob(chunksRef.current, { type: mime });
        if (blob.size < 1024) {
          setError("That recording was too short — please try again.");
          return;
        }
        await upload(blob, mime);
      };
      rec.start();
      recorderRef.current = rec;
      setRecording(true);
      setElapsed(0);
      timerRef.current = setInterval(() => setElapsed((s) => s + 1), 1000);
    } catch (err) {
      setError(
        "Microphone access was blocked. Tap the lock icon in your browser and allow microphone for this site.",
      );
    }
  }

  function stop() {
    setRecording(false);
    recorderRef.current?.stop();
    recorderRef.current = null;
  }

  async function upload(blob: Blob, mime: string) {
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("token", token);
      fd.append("file", blob, `recording.${extFor(mime)}`);
      const res = await fetch("/api/public/interview/transcribe", {
        method: "POST",
        body: fd,
      });
      const json = await res.json();
      if (!res.ok) {
        setError(json?.error ?? "Transcription failed.");
        return;
      }
      const t = String(json.transcript ?? "").trim();
      const next = transcript ? `${transcript} ${t}`.trim() : t;
      setTranscript(next);
      onTranscript(next);
    } catch (err) {
      setError("Network problem — please try again.");
    } finally {
      setBusy(false);
    }
  }

  const mm = String(Math.floor(elapsed / 60)).padStart(2, "0");
  const ss = String(elapsed % 60).padStart(2, "0");

  return (
    <div className="mt-8">
      <div className="flex flex-col items-center">
        {!recording ? (
          <button
            type="button"
            disabled={busy}
            onClick={start}
            className="flex h-24 w-24 items-center justify-center rounded-full bg-[#D97706] text-white shadow-lg active:translate-y-px disabled:opacity-50"
            aria-label="Start recording"
          >
            <Mic className="h-10 w-10" />
          </button>
        ) : (
          <button
            type="button"
            onClick={stop}
            className="relative flex h-24 w-24 items-center justify-center rounded-full bg-[#D97706] text-white shadow-lg"
            aria-label="Stop recording"
          >
            <span className="absolute inset-0 animate-ping rounded-full bg-[#D97706]/40" />
            <Square className="h-9 w-9 fill-current" />
          </button>
        )}
        <p className="mt-3 text-sm font-medium text-slate-600">
          {busy
            ? "Transcribing…"
            : recording
              ? `Listening… ${mm}:${ss}`
              : transcript
                ? "Tap mic to add more, or edit below"
                : "Tap mic to answer"}
        </p>
      </div>

      {error ? (
        <p className="mt-3 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>
      ) : null}

      <div className="mt-6">
        <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
          Here's what we heard — edit if needed
        </label>
        <textarea
          value={transcript}
          onChange={(e) => {
            setTranscript(e.target.value);
            onTranscript(e.target.value);
          }}
          rows={6}
          className="mt-2 w-full rounded-xl border border-slate-200 bg-white p-3 text-base text-[#0F1A42] focus:border-[#D97706] focus:outline-none"
          placeholder="Your answer will appear here after recording."
        />
        {transcript ? (
          <button
            type="button"
            onClick={() => {
              setTranscript("");
              onTranscript("");
            }}
            className="mt-2 text-sm font-medium text-slate-500 underline"
          >
            Clear & re-record
          </button>
        ) : null}
      </div>
    </div>
  );
}
